import { Component, HostBinding, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ConditionalAccessPolicy, NamedLocation } from '@microsoft/microsoft-graph-types-beta';
import { first } from 'rxjs/operators';
import {
    MultiSelectorDialogComponent,
    MultiSelectorDialogData,
} from 'projects/angular-clarity/src/app/modules/shared/components/multi-selector-dialog/multi-selector-dialog.component';

interface genericConfigurations {
    configureNetwork: boolean;
    includeLocations: string;
    excludeLocations: string;
}

@Component({
    selector: 'cap-network-control',
    templateUrl: './cap-network-control.component.html',
    styleUrls: ['./cap-network-control.component.scss', '../../../../../styles/baseline-style.scss'],
})
export class CapNetworkControlComponent implements OnInit, OnChanges {
    @HostBinding('style.display') display = 'contents';

    @Input() policy: ConditionalAccessPolicy;
    @Input() readOnly: boolean;
    @Input() form: FormGroup;
    @Input() errorsString: string = '';

    @Input() namedLocations: NamedLocation[] = [];

    @Input() override = [];

    currentIncludeLocations: string[] = [];
    currentExcludeLocations: string[] = [];

    genericConfigurations: genericConfigurations = {
        configureNetwork: false,
        includeLocations: '',
        excludeLocations: '',
    };

    constructor(private dialog: MatDialog) {}
    ngOnChanges(changes: SimpleChanges): void {
        this.setInitialData();
    }

    ngOnInit(): void {
        this.setInitialData();
    }
    setInitialData() {
        this.currentIncludeLocations =
            this.policy?.conditions?.locations?.includeLocations?.map(this.getLocation) || [];
        this.currentExcludeLocations =
            this.policy?.conditions?.locations?.excludeLocations?.map(this.getLocation) || [];

        this.genericConfigurations.configureNetwork = this.includeLocationsNames?.length > 0;
        if (this.genericConfigurations.configureNetwork) {
            this.genericConfigurations.includeLocations = this.includeLocationValues?.includes('All')
                ? 'All'
                : this.includeLocationValues?.includes('AllTrusted')
                  ? 'AllTrusted'
                  : 'any';
            this.genericConfigurations.excludeLocations = this.excludeLocationValues?.includes('AllTrusted')
                ? 'AllTrusted'
                : 'any';
        }
    }

    selectLocations(type: 'includeLocations' | 'excludeLocations') {
        const dialogRef = this.dialog.open(MultiSelectorDialogComponent, {
            data: <MultiSelectorDialogData>{
                items: this.namedLocations.map((res) => ({ id: res.id, name: res.displayName })),
                title: 'Select networks',
                noDataMessage: 'No named locations found',
            },
            width: '40%',
            height: '100vh',
            position: {
                top: '0',
                right: '0',
            },
        });

        dialogRef
            .afterClosed()
            .pipe(first())
            .subscribe((res: string[]) => {
                if (res instanceof Array) {
                    this.locationsController.get(type).setValue(res || []);
                }
            });
    }

    onToggleNetworkChange() {
        this.locationsController.get('includeLocations').setValue([]);

        this.locationsController.get('excludeLocations').setValue([]);
    }

    getLocation = (location: string) => {
        // must be an arrow function;
        if (['All', 'AllTrusted'].includes(location)) {
            return {
                All: 'Any network or location',
                AllTrusted: 'All trusted networks and locations',
            }[location];
        }

        return this.namedLocations.find((res) => res.id === location)?.displayName || location;
    };

    public onIncludeLocationRadioGroupChange(): void {
        const includeLocations = ['All', 'AllTrusted'].includes(this.genericConfigurations.includeLocations)
            ? [this.genericConfigurations.includeLocations]
            : [];

        this.locationsController.get('includeLocations').setValue(includeLocations);
    }

    public onExcludeLocationRadioGroupChange(): void {
        const excludeLocations = ['AllTrusted'].includes(this.genericConfigurations.excludeLocations)
            ? [this.genericConfigurations.excludeLocations]
            : [];

        this.locationsController.get('excludeLocations').setValue(excludeLocations);
    }

    get includeLocationsNames(): string[] {
        return this.includeLocationValues.map(this.getLocation);
    }

    get includeLocationValues(): string[] {
        return (this.locationsController.get('includeLocations').value as string[]) || [];
    }

    get excludeLocationsNames(): string[] {
        return this.excludeLocationValues.map(this.getLocation);
    }

    get excludeLocationValues(): string[] {
        return (this.locationsController.get('excludeLocations').value as string[]) || [];
    }

    get locationsController(): FormGroup {
        return this.form.get('conditions').get('locations') as FormGroup;
    }
}
